/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {
	BaseComponent,
	Dialog_Builder,
	DialogModule,
	TS_Input
} from "@intuitionrobotics/thunderstorm/frontend";
import * as React from "react";
import * as emotion from "emotion";
import {COLORS} from "@res/colors";
import {_keys} from "@intuitionrobotics/ts-common";
import {
	AlertFeedback,
	DB_Alert,
	Feedback_KeyFreeText,
	FeedbackCategory,
	FeedbackCategoryOption,
	getAlertDetails,
	getAlertTitle
} from "@app/app-shared/alert";
import {Checkbox} from "../pages/ui/patients/Checkbox";
import {FONTS} from "@res/fonts";
import {ErrorToast} from "../themes/toasts";
import {OnAlertsUpdated} from "@modules/AlertsModule";
import {Loader} from "../../widgets/Loader";

const feedbackHeader = emotion.css(
	{
		height: 60,
		padding: "25px 32px",
		boxSizing: "border-box",
		borderBottom: `1px solid ${COLORS.neutral_gray_2}`
	}
);

const feedbackBody = emotion.css(
	{
		overflow: "auto",
		padding: "0px 32px",
		boxSizing: "border-box",
		flexGrow: 1
	}
);

const feedbackFooter = emotion.css(
	{
		padding: "40px 32px 32px 32px",
		boxSizing: "border-box",
		justifyContent: "flex-end"
	}
);

const categoriesWrapper = emotion.css(
	{
		flexGrow: 1,
		boxSizing: "border-box"
	});

const alertDescriptionClass = emotion.css(
	{
		marginTop: 19,
		marginBottom: 20,
		height: 46
	}
);

const categoryWrapper = (last: boolean) => emotion.css(
	{
		boxSizing: "border-box",
		marginBottom: last ? undefined : 8,
		border: `1px solid ${COLORS.neutral_gray_2}`,
		padding: 13

	}
);

const categoryLabel = emotion.css(
	{
		minWidth: "30%",
		width: "30%",
		boxSizing: "border-box",
		color: COLORS.neutral_text_dark
	}
);

const categoryOptionsWrapper = emotion.css(
	{
		flexGrow: 1
	}
);

const categoryOption = (last: boolean) => emotion.css(
	{
		marginBottom: last ? undefined : 11,
		minHeight: 20
	}
);

const otherFreeTextInput = emotion.css(
	{
		width: "100%",
		boxSizing: "border-box",
		borderBottom: `1px solid ${COLORS.neutral_gray_2}`,
		borderRadius: 0
	}
);

const okButton = (enabled: boolean) => emotion.css(
	{
		height: 40,
		width: 180,
		textAlign: "center",
		padding: "10px 11px",
		borderRadius: "4px",
		color: COLORS.white,
		userSelect: "none",
		cursor: enabled ? "pointer" : "default",
		backgroundColor: enabled ? COLORS.primary_blue_hover : COLORS.neutral_gray_2,
		boxSizing: "border-box"
	}
);

const cancelButton = emotion.css(
	{
		height: 40,
		width: 180,
		// Center text vertically. Resolved content height == 17.
		lineHeight: "17px",
		textAlign: "center",
		padding: "10px 11px",
		borderRadius: "4px",
		userSelect: "none",
		cursor: "pointer",
		border: `1px solid ${COLORS.neutral_gray_1}`,
		boxSizing: "border-box",
		marginRight: 13
	}
);

type State = {
	freeText?: string;
	selections: { [categoryKey: string]: string[] | undefined }
	loading: boolean
	error: boolean
}

type Props = {
	alert: DB_Alert<any>;
	feedbackCategories: FeedbackCategory[];
	onSubmit: (feedback: AlertFeedback) => void;

}

export class Dialog_AlertFeedback
	extends BaseComponent<Props, State>
	implements OnAlertsUpdated {

	public static show(
		feedbackCategories: FeedbackCategory[],
		alert: DB_Alert<any>,
		onSubmit: (feedback: AlertFeedback) => void
	) {
		new Dialog_Builder(
			<Dialog_AlertFeedback
				feedbackCategories={feedbackCategories}
				alert={alert}
				onSubmit={onSubmit}
			/>)
			.setAllowIndirectClosing(true)
			.show();
	}

	constructor(props: Props) {
		super(props);
		this.state = {
			selections: {},
			loading: false,
			error:false
		};
	}


	__onAlertsUpdated() {
		DialogModule.close();
	}

	private onFeedbackSubmit = () => {
		const feedback: AlertFeedback = {
			...this.state.selections
		};
		if (this.state.freeText)
			feedback[Feedback_KeyFreeText] = [this.state.freeText];

		if (!_keys(feedback).some(key => feedback[key])) {
			this.logError("Attempted to submit empty feedback.");
			return ErrorToast("You must provide feedback for the alert.");
		}

		this.props.onSubmit(feedback);
	};

	private onFeedbackOptionClick = (categoryKey: string, categoryOptionKey: string) => {
		// No option for this category key was selected so far.
		const currentCategoryKeys = this.state.selections[categoryKey];
		if (!currentCategoryKeys || !Array.isArray(currentCategoryKeys)) {
			return this.setState(
				{
					selections: {
						...this.state.selections,
						[categoryKey]: [categoryOptionKey]
					}
				});
		}

		const existingOptions = [...currentCategoryKeys];
		const index: number = currentCategoryKeys.indexOf(categoryOptionKey);
		// The option was not selected for this category.
		if (index === -1) {
			existingOptions.push(categoryOptionKey);
		} else {
			existingOptions.splice(index, 1);
		}

		return this.setState(
			{
				selections: {
					...this.state.selections,
					[categoryKey]: existingOptions.length ? existingOptions : undefined
				}
			});
	};

	render() {
		return <div className={`ll_v_l`} style={{width: 849, maxHeight: "90vh"}}>
			{this.state.loading && <Loader overlay={true}/>}
			{this.renderHeader()}
			{this.renderBody()}
			{this.state.error && this.renderErrorMessage()}
			{this.renderFooter()}
		</div>;
	}

	private renderCategories = () => {
		return <div id={"categories-wrapper"}
		            className={`ll_v_l match_width ${categoriesWrapper}`}>
			<div style={{marginBottom: 7, color: COLORS.primary_dark_blue, fontWeight: 500, fontSize: 16}}>
				What actions did you take as a result of this notification?
			</div>
			{
				this.props.feedbackCategories.map((category, i) => {
					return this.renderCategory(category, i);
				})
			}
		</div>;
	};

	private renderCategory = (category: FeedbackCategory, idx: number) => {
		const last = idx + 1 === this.props.feedbackCategories.length;
		return <div key={category.key}
		            id={`category-${category.key}`}
		            className={`ll_h_t match_width ${categoryWrapper(last)}`}>
			<div id={`category-${category.key}-label`}
			     className={`${categoryLabel}`}>
				{category.label}
			</div>
			{this.renderCategoryOptions(category.options, category.key)}
		</div>;
	};

	private renderCategoryOptions = (options: FeedbackCategoryOption[], categoryKey: string) => {
		return <div id={`${categoryKey}-category-options-wrapper`}
		            className={`ll_v_l ${categoryOptionsWrapper}`}>
			{
				options.map((option, i) => {
					const isLast = i + 1 === options.length;
					return this.renderCategoryOption(i, categoryKey, option, isLast);
				})
			}
		</div>;
	};

	private renderCategoryOption = (i: number, categoryKey: string, option: FeedbackCategoryOption, isLast: boolean) => {
		if (option.key === Feedback_KeyFreeText)
			return <div key={option.key} className={`match_width ll_h_c ${categoryOption(isLast)}`}>
				<div style={{marginRight: 8, minWidth: 16, height: 16}}>
					<Checkbox
						on={this.state?.freeText !== undefined}
						onChange={
							() => {
								this.setState(state => ({freeText: state.freeText === undefined ? "" : undefined}));
							}
						}/>
				</div>
				<div className={"ll_h_c"} style={{flexGrow: 1}}>
					<div style={{marginRight: 8}}>{`Other: `}</div>
					<TS_Input
						value={this.state.freeText}
						onChange={value => this.setState({freeText: value.trim()})}
						type={"text"}
						id={"category-feedback-other-free-text"}
						className={`${otherFreeTextInput}`}
						placeholder={"Type here"}/>
				</div>
			</div>;

		const isChecked = !!(this.state.selections[categoryKey] &&
			this.state.selections[categoryKey]?.includes(option.key));
		return (
			<div key={option.key} className={`match_width ll_h_c ${categoryOption(isLast)}`}>
				<div style={{marginRight: 8, minWidth: 16, height: 16}}>
					<Checkbox
						on={isChecked}
						onChange={
							() => this.onFeedbackOptionClick(categoryKey, option.key)
						}/>
				</div>
				<div style={{flexGrow: 1, fontWeight: isChecked ? 500 : 'normal', color: COLORS.neutral_text_dark}}>
					{option.label}
				</div>
			</div>
		);
	};

	private renderAlertDescription = () => {
		const title = getAlertTitle(this.props.alert);
		const desc = getAlertDetails(this.props.alert);
		return <div className={`ll_v_s ${alertDescriptionClass}`} style={{justifyContent: "space-between"}}>
			{title && FONTS.medium_18(title)}
			{desc && FONTS.reg_16(`${desc}`)}
		</div>;
	};

	private renderHeader = () => {
		return <div id={"feedback-header"}
		            className={`match_width ${feedbackHeader} ll_h_c`}>
			<b style={{
				// These should be replaced when the correct font is available.
				color: COLORS.primary_dark_blue,
				fontSize: 18,
				lineHeight: 20,
				letterSpacing: 0,
				fontWeight: 500
			}}>
				Notification result</b>
		</div>;
	};

	private renderBody = () => {
		return <div id={"feedback-body"}
		            className={`match_width ll_v_l ${feedbackBody}`}>
			{this.renderAlertDescription()}
			{this.renderCategories()}
		</div>;
	};

	private renderErrorMessage = () => {
		return <div style={{backgroundColor: COLORS.secondary_red_10, width: '100%', height: '50px', textAlign: 'center', borderRadius: '4px'}}>
			{FONTS.reg_14_secondary_dark('An error occurred while resolving this alert. Please try again later.', {color: COLORS.secondary_red, padding: '5px'})}
		</div>;
	};

	private renderFooter = () => {
		const enabled = !!(this.state.freeText || _keys(this.state.selections).some(key => this.state.selections[key]));
		return <div id={"feedback-footer"}
		            className={`match_width ${feedbackFooter}`}>
			<div className={"ll_h_t"} style={{justifyContent: "flex-end"}}>
				<div className={`${cancelButton}`}
				     onClick={() => DialogModule.close()}>
					Cancel
				</div>
				<div className={`${okButton(enabled)}`}
				     onClick={() => {
					     if (!enabled)
						     return;
					     this.setState({loading: true});
					     this.onFeedbackSubmit();
				     }}>
					Mark as resolved
				</div>
			</div>
		</div>;
	};

}
